import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Button, Card, CardBody, CardProps, Flex, Heading, Link } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { CopyableField } from "components/DataDisplay/Copyable";
import CDS, { FloatingField } from "design-system";
import { PARTNER_INSTRUCTION_URLS } from "definitions/constants";
import { useCreateIntegrationWithSecret } from "features/Integrations/hooks";
import { webhookUrl } from "helpers/integrations";
import { titleize } from "helpers/string";
import { NewIncomingWebhook } from "models/integration/incomingWebhook";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

type NewCredentialsProps = {
  webhookCredentials: NewIncomingWebhook;
};

function Instructions({ webhookCredentials }: NewCredentialsProps) {
  return (
    <CDS.Text14 color="fg.muted">
      {`For instructions on setting up the integration,
      please visit `}
      <Link href={PARTNER_INSTRUCTION_URLS[webhookCredentials.partner]} isExternal>
        {"the setup instructions. "}
        <ExternalLinkIcon mb="4px" mx="2px" />
      </Link>
    </CDS.Text14>
  );
}

function SecretGivenDisplay({ webhookCredentials }: NewCredentialsProps) {
  const { partner, secret } = webhookCredentials;

  if (!PARTNER_INSTRUCTION_URLS[partner]) {
    console.error(`Instruction URLs constant missing a URL for partner ${partner}`);
  }

  const partnerName = titleize(partner);

  return (
    <>
      <Heading size="xs" color="fg.muted">{`${partnerName} Key`}</Heading>
      <CDS.Text14 color="fg.muted" fontWeight="700">
        This secret is only available upon creation.
      </CDS.Text14>
      <CDS.Text14 color="fg.muted" fontWeight="700">
        After you close this webpage, you will not be able to access it!
      </CDS.Text14>
      <Instructions webhookCredentials={webhookCredentials} />
      <CopyableField includeFieldName fieldName="Webhook Url" fieldValue={webhookUrl(webhookCredentials)} />
      <CopyableField includeFieldName fieldName="Secret" fieldValue={secret} />
    </>
  );
}

const schema = yup.object({
  secret: yup.string().trim().label("Secret").required(),
});

export type Schema = yup.InferType<typeof schema>;

function CredentialsInputDisplay({ webhookCredentials }: NewCredentialsProps) {
  const { id, partner, event } = webhookCredentials;

  if (!PARTNER_INSTRUCTION_URLS[partner]) {
    console.error(`Instruction URLs constant missing a URL for partner ${partner}`);
  }

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setFocus,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: { id, partner, event },
  });

  const { mutate, isSuccess } = useCreateIntegrationWithSecret();

  useEffect(() => {
    setFocus("secret");
  }, [reset, setFocus]);

  const partnerName = titleize(partner);

  return (
    <>
      <Heading size="xs" color="fg.muted">{`${partnerName} Credentials`}</Heading>
      <CDS.Text14 color="fg.muted" fontWeight="700">
        Please input the secret before you close this webpage - Otherwise, you will need to recreate the credentials!
      </CDS.Text14>
      <Instructions webhookCredentials={webhookCredentials} />
      <CopyableField includeFieldName fieldName="Webhook Url" fieldValue={webhookUrl(webhookCredentials)} />
      {isSuccess ? (
        <CDS.Text14 color="fg.muted" fontWeight="700">
          Secret successfully added!
        </CDS.Text14>
      ) : (
        <Flex direction="row" gap="4" align="center" justify="center">
          <FloatingField name="secret" errors={errors.secret?.message} required>
            <FloatingField.Label>Secret</FloatingField.Label>
            <FloatingField.Text control={control} name="secret" />
          </FloatingField>
          <Button onClick={handleSubmit(mutate)}>Add Secret</Button>
        </Flex>
      )}
    </>
  );
}

export default function NewCredentialsCard({ webhookCredentials, ...rest }: NewCredentialsProps & CardProps) {
  const { partner } = webhookCredentials;

  if (!PARTNER_INSTRUCTION_URLS[partner]) {
    console.error(`Instruction URLs constant missing a URL for partner ${partner}`);
  }

  return (
    <Card maxWidth="800px" mb="4" {...rest}>
      <CardBody>
        <Flex direction="column" gap={4}>
          {partner === "greenhouse" ? (
            <SecretGivenDisplay webhookCredentials={webhookCredentials} />
          ) : (
            <CredentialsInputDisplay webhookCredentials={webhookCredentials} />
          )}
        </Flex>
      </CardBody>
    </Card>
  );
}
